/**
 * Audit Log Report
 *
 * One row per audit entry. Expand/collapse reveals field-level changes.
 * - 0 changes  → single collapsed row, no chevron
 * - 1 change   → inline Before/After on the collapsed row, no chevron needed
 * - 2+ changes → collapsed row shows "Field1, Field2 changed" summary + chevron
 *
 * Excel export flattens to one row per field change (independent of on-screen view).
 *
 * EDS-compliant: semantic tokens, EDS shadcn components, 4 px spacing scale.
 */
import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { format, parseISO, isValid, startOfDay, endOfDay } from 'date-fns';
import {
  ClipboardList,
  Download,
  RotateCcw,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronRight as ChevronRightExpand,
  SlidersHorizontal,
  Settings2,
  GripVertical,
  Filter,
  Check,
  X,
  ShieldAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { HelpButton } from '@/components/help/HelpButton';
import { TablePagination } from '@/components/TablePagination';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { PageLoadingOverlay } from '@/components/PageLoadingOverlay';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';

import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetHeader, SheetFooter } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';

import {
  useAudit,
  fetchAuditLogEntries,
  AuditLogFetchError,
  DEFAULT_AUDIT_CATEGORIES,
  type AuditLogEntry,
  type AuditAction,
  type AuditCategory,
} from '@/contexts/AuditContext';
import { useBookingDatabase } from '@/contexts/BookingDatabaseContext';
import { useDataMode } from '@/contexts/DataModeContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useScheduler } from '@/contexts/SchedulerContext';
import { useReportScope } from '@/hooks/useReportScope';
import { useScrollbarGutter } from '@/hooks/useScrollbarGutter';
import { getNavMonths } from '@/lib/datePickerNav';
import {
  RAW_TABLE_CONTAINER_CLASS,
  RAW_TABLE_SCROLL_CLASS,
  TABLE_HEADER_ROW_CLASS,
  RAW_TABLE_CLASS,
  TABLE_HOVER_BG,
  alternatingRowBackground,
} from '@/lib/table-styles';

// ─── helpers ─────────────────────────────────────────────────────────────────

function safeParse(iso: string): Date | null {
  try {
    const d = parseISO(iso);
    return isValid(d) ? d : null;
  } catch {
    return null;
  }
}

function formatDateTime(iso: string): string {
  const d = safeParse(iso);
  if (!d) return iso;
  return format(d, 'dd MMM yyyy, HH:mm');
}

function changeTypeToAction(ct: string): AuditAction {
  if (ct === 'created') return 'New';
  if (ct === 'deleted') return 'Delete';
  return 'Edit';
}

/** Human-readable summary of which fields changed, e.g. "Start Date, Status changed" */
function fieldSummary(entry: AuditLogEntry): string {
  if (entry.changes.length === 0) return '';
  const names = entry.changes.map((c) => c.field).join(', ');
  return `${names} changed`;
}

type SortField = 'timestamp' | 'user' | 'category' | 'action' | 'entity';
type SortDir = 'asc' | 'desc';

// ─── Action badge ─────────────────────────────────────────────────────────────

function ActionBadge({ action }: { action: AuditAction }) {
  if (action === 'New') {
    return (
      <Badge variant="success" accent="success" className="text-xs font-medium">
        New
      </Badge>
    );
  }
  if (action === 'Delete') {
    return (
      <Badge variant="destructive" accent="destructive" className="text-xs font-medium">
        Delete
      </Badge>
    );
  }
  return (
    <Badge variant="warning" accent="warning" className="text-xs font-medium">
      Edit
    </Badge>
  );
}

// ─── Before cell ─────────────────────────────────────────────────────────────

function BeforeCell({
  action,
  value,
}: {
  action: AuditAction;
  value: string | null | undefined;
}) {
  if (action === 'New') {
    return <span className="text-[13px] text-muted-foreground/40 italic select-none">—</span>;
  }
  if (value == null || value === '') {
    return <span className="text-[13px] text-muted-foreground/40 italic">(empty)</span>;
  }
  return (
    <span className="text-[13px] text-muted-foreground line-through opacity-70 break-words">
      {value}
    </span>
  );
}

// ─── After cell ──────────────────────────────────────────────────────────────

function AfterCell({
  action,
  value,
}: {
  action: AuditAction;
  value: string | null | undefined;
}) {
  if (action === 'Delete') {
    return (
      <Badge variant="destructive" accent="destructive" className="text-[11px] font-medium">
        Deleted
      </Badge>
    );
  }
  if (value == null || value === '') {
    return <span className="text-[13px] text-muted-foreground/40 italic">(empty)</span>;
  }
  return (
    <span className="text-[13px] text-foreground font-medium break-words">{value}</span>
  );
}

// ─── Category pill ────────────────────────────────────────────────────────────

function CategoryPill({ label }: { label: string }) {
  return (
    <span
      className="inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium whitespace-nowrap"
      style={{
        background: 'var(--color-background-secondary, var(--muted))',
        color: 'var(--muted-foreground)',
        border: '1px solid var(--color-border-tertiary, var(--border))',
      }}
    >
      {label}
    </span>
  );
}

// ─── component ───────────────────────────────────────────────────────────────

export function AuditLogReport() {
  const { entries: auditEntries } = useAudit();
  const { bookingHistory } = useBookingDatabase();
  const { getScreenMode } = useDataMode();
  const { canExportReport } = usePermissions();
  const { resources } = useScheduler();
  // Scope which users' activity is visible by the role's report "View Access":
  //  - 'all'  → every user's entries (allowedIdentities === null)
  //  - 'team' → entries by users in the logged-in user's department
  //  - 'own'  → only the logged-in user's own entries
  const { access: viewAccess, getUserIdentities } = useReportScope('auditLogReport');
  const allowedIdentities = useMemo(() => getUserIdentities(resources), [getUserIdentities, resources]);
  const isEntryVisible = useCallback(
    (entryUser: string | null | undefined) =>
      !allowedIdentities || allowedIdentities.has((entryUser ?? '').trim().toLowerCase()),
    [allowedIdentities],
  );
  const canExport = canExportReport('auditLogReport');
  const isLive = getScreenMode('settings') === 'live';

  // ── Live mode state ─────────────────────────────────────────────────────────
  const [liveEntries, setLiveEntries] = useState<AuditLogEntry[]>([]);
  const [liveTotal, setLiveTotal] = useState(0);
  const [liveLoading, setLiveLoading] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const fetchIdRef = useRef(0);

  // Project booking history records into AuditLogEntry shape (no duplication)
  const bookingAuditEntries = useMemo<AuditLogEntry[]>(() => {
    return bookingHistory.map((h) => ({
      id: `bh-${h.id}`,
      timestamp: h.changedAt,
      user: h.changedByName,
      category: 'Bookings' as AuditCategory,
      action: changeTypeToAction(h.changeType),
      entity: h.bookingId,
      changes: (h.changedFields || []).map((cf) => ({
        field: cf.fieldName,
        before: cf.oldValue != null ? String(cf.oldValue) : null,
        after: cf.newValue != null ? String(cf.newValue) : null,
      })),
    }));
  }, [bookingHistory]);

  // Merge: booking projections + all non-booking audit entries
  const allEntries = useMemo<AuditLogEntry[]>(() => {
    const nonBooking = auditEntries.filter((e) => e.category !== 'Bookings');
    const bookingFromAudit = auditEntries.filter((e) => e.category === 'Bookings');
    return [
      ...bookingAuditEntries,
      ...bookingFromAudit,
      ...nonBooking,
    ].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  }, [auditEntries, bookingAuditEntries]);

  // ── Filter state ────────────────────────────────────────────────────────────
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [filterUser, setFilterUser] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterAction, setFilterAction] = useState<string>('all');
  const [filterKeyword, setFilterKeyword] = useState('');

  // ── Sort state ──────────────────────────────────────────────────────────────
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // ── Column visibility ────────────────────────────────────────────────────────
  const [colConfigOpen, setColConfigOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  type AuditColKey = 'user' | 'category' | 'action' | 'entity' | 'before' | 'after';
  const ALL_AUDIT_COLS: { key: AuditColKey; label: string; description: string }[] = [
    { key: 'user',     label: 'User',     description: 'User who performed the action'    },
    { key: 'category', label: 'Category', description: 'Area of the application affected' },
    { key: 'action',   label: 'Action',   description: 'Type of change (New / Edit / Delete)' },
    { key: 'entity',   label: 'Entity',   description: 'Record name or description'       },
    { key: 'before',   label: 'Before',   description: 'Value before the change'          },
    { key: 'after',    label: 'After',    description: 'Value after the change'           },
  ];
  const [visibleAuditCols, setVisibleAuditCols] = useState<Set<AuditColKey>>(
    new Set(['user','category','action','entity','before','after'])
  );
  const toggleAuditCol = (key: AuditColKey) => setVisibleAuditCols(prev => {
    const next = new Set(prev);
    if (next.has(key)) { if (next.size > 1) next.delete(key); } else next.add(key);
    return next;
  });
  const resetAuditCols = () => setVisibleAuditCols(new Set(['user','category','action','entity','before','after']));

  // ── Pagination (over entries) ────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const resetPage = useCallback(() => setPage(1), []);

  // ── Live-mode fetcher ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!isLive) return;
    const id = ++fetchIdRef.current;
    setLiveLoading(true);
    fetchAuditLogEntries({
      category: filterCategory !== 'all' ? filterCategory : undefined,
      action: filterAction !== 'all' ? filterAction : undefined,
      user: filterUser || undefined,
      from: dateFrom ? startOfDay(dateFrom).toISOString() : undefined,
      to: dateTo ? endOfDay(dateTo).toISOString() : undefined,
      keyword: filterKeyword || undefined,
      page,
      pageSize,
      sortField,
      sortDir,
    })
      .then((result) => {
        if (id !== fetchIdRef.current) return;
        setPermissionDenied(false);
        setLiveEntries(result.entries);
        setLiveTotal(result.total);
      })
      .catch((err) => {
        if (id !== fetchIdRef.current) return;
        setLiveEntries([]);
        setLiveTotal(0);
        if (err instanceof AuditLogFetchError && err.status === 403) {
          setPermissionDenied(true);
        } else {
          setPermissionDenied(false);
          console.error('Failed to fetch audit entries:', err);
        }
      })
      .finally(() => {
        if (id === fetchIdRef.current) setLiveLoading(false);
      });
  }, [isLive, filterCategory, filterAction, filterUser, dateFrom, dateTo, filterKeyword, page, pageSize, sortField, sortDir]);

  // ── Expand state — set of entry IDs currently expanded ───────────────────
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // ── Derived options ─────────────────────────────────────────────────────────
  const userOptions = useMemo(
    () => {
      const source = (isLive ? liveEntries : allEntries).filter((e) => isEntryVisible(e.user));
      return [...new Set(source.map((e) => e.user))].sort();
    },
    [isLive, liveEntries, allEntries, isEntryVisible],
  );
  const categoryOptions = useMemo(
    () => {
      if (isLive) return DEFAULT_AUDIT_CATEGORIES.map(c => c.category).sort();
      return [...new Set(allEntries.map((e) => e.category))].sort();
    },
    [isLive, allEntries],
  );

  // ── Filtered entries ────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return allEntries.filter((e) => {
      if (!isEntryVisible(e.user)) return false;
      if (dateFrom) {
        const d = safeParse(e.timestamp);
        if (!d || d < startOfDay(dateFrom)) return false;
      }
      if (dateTo) {
        const d = safeParse(e.timestamp);
        if (!d || d > endOfDay(dateTo)) return false;
      }
      if (filterUser && !e.user.toLowerCase().includes(filterUser.toLowerCase()))
        return false;
      if (filterCategory !== 'all' && e.category !== filterCategory) return false;
      if (filterAction !== 'all' && e.action !== filterAction) return false;
      if (filterKeyword) {
        const kw = filterKeyword.toLowerCase();
        const inEntity = e.entity.toLowerCase().includes(kw);
        const inChanges = e.changes.some(
          (c) =>
            c.field.toLowerCase().includes(kw) ||
            (c.before ?? '').toLowerCase().includes(kw) ||
            (c.after ?? '').toLowerCase().includes(kw),
        );
        if (!inEntity && !inChanges) return false;
      }
      return true;
    });
  }, [allEntries, dateFrom, dateTo, filterUser, filterCategory, filterAction, filterKeyword, isEntryVisible]);

  // ── Sorted ──────────────────────────────────────────────────────────────────
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'timestamp': {
          const ta = new Date(a.timestamp).getTime();
          const tb = new Date(b.timestamp).getTime();
          cmp = (isNaN(ta) ? 0 : ta) - (isNaN(tb) ? 0 : tb);
          break;
        }
        case 'user': cmp = a.user.localeCompare(b.user); break;
        case 'category': cmp = a.category.localeCompare(b.category); break;
        case 'action': cmp = a.action.localeCompare(b.action); break;
        case 'entity': cmp = a.entity.localeCompare(b.entity); break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortField, sortDir]);

  // In live mode entries are server-paginated; when the role restricts the
  // report to 'team'/'own', additionally filter the current page client-side so
  // no other users' activity is shown. (For 'all', the unfiltered server page
  // and total are used.)
  const visibleLiveEntries = useMemo(
    () => (viewAccess === 'all' ? liveEntries : liveEntries.filter((e) => isEntryVisible(e.user))),
    [liveEntries, viewAccess, isEntryVisible],
  );
  const liveVisibleTotal = viewAccess === 'all' ? liveTotal : visibleLiveEntries.length;

  // ── Paginated entries ────────────────────────────────────────────────────────
  const totalRecords = isLive ? liveVisibleTotal : sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const paginatedEntries = useMemo(() => {
    if (isLive) return visibleLiveEntries;
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [isLive, visibleLiveEntries, sorted, page, pageSize]);

  // Keep the page in range when filters or a larger page size shrink the result set
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        // Toggle between asc and desc
        setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortDir('asc');
      }
      resetPage();
    },
    [sortField, sortDir, resetPage],
  );

  const hasActiveFilters =
    !!dateFrom || !!dateTo || !!filterUser ||
    filterCategory !== 'all' || filterAction !== 'all' || !!filterKeyword;

  const clearFilters = useCallback(() => {
    setDateFrom(undefined);
    setDateTo(undefined);
    setFilterUser('');
    setFilterCategory('all');
    setFilterAction('all');
    setFilterKeyword('');
    resetPage();
  }, [resetPage]);

  // ── Excel export — one row per field change ──────────────────────────────
  const handleExportExcel = useCallback(() => {
    const rows: Record<string, string>[] = [];
    for (const entry of sorted) {
      if (entry.changes.length === 0) {
        rows.push({
          'Date/Time': formatDateTime(entry.timestamp),
          User: entry.user,
          Category: entry.category,
          Action: entry.action,
          Entity: entry.entity,
          Field: '',
          Before: '',
          After: entry.action === 'Delete' ? 'Deleted' : '',
        });
      } else {
        for (const ch of entry.changes) {
          rows.push({
            'Date/Time': formatDateTime(entry.timestamp),
            User: entry.user,
            Category: entry.category,
            Action: entry.action,
            Entity: entry.entity,
            Field: ch.field,
            Before: entry.action === 'New' ? '' : (ch.before ?? ''),
            After: entry.action === 'Delete' ? 'Deleted' : (ch.after ?? ''),
          });
        }
      }
    }
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Audit Log');
    const filename = `audit-log-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    XLSX.writeFile(workbook, filename);
    toast.success('Audit log exported', {
      description: `${rows.length} rows exported to Excel.`,
    });
  }, [sorted]);

  // ── Sort icon ──────────────────────────────────────────────────────────────
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field)
      return <ArrowUpDown className="ml-1 h-3 w-3 text-muted-foreground/50 inline-block shrink-0" />;
    return sortDir === 'asc'
      ? <ArrowUp className="ml-1 h-3 w-3 text-primary inline-block shrink-0" />
      : <ArrowDown className="ml-1 h-3 w-3 text-primary inline-block shrink-0" />;
  };

  const breadcrumbItems = [{ label: 'Reports' }, { label: 'Audit Log' }];

  const { ref: contentRef, gutter: contentGutter } = useScrollbarGutter<HTMLDivElement>();
  const gutterPadding = { paddingRight: `calc(1.5rem + ${contentGutter}px)` };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">

      {/* ── Header ── */}
      <div
        className="flex items-center justify-between px-6 py-4 shrink-0"
        style={gutterPadding}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
            <ClipboardList className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <Breadcrumb items={breadcrumbItems} className="text-[12px] text-muted-foreground mb-0.5" />
            <h1 className="text-xl font-semibold text-foreground leading-tight">Audit Log</h1>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              All create, edit and delete activity across the application
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-4">
          <FeatureFeedbackCollector feature="Audit Log" />
          <HelpButton />
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="px-6 py-2.5 shrink-0 flex items-center gap-2" style={gutterPadding}>
        {/* Record count + active filter chips */}
        <span className="text-[12px] text-muted-foreground shrink-0">
          {(isLive ? liveVisibleTotal : filtered.length).toLocaleString()} record{(isLive ? liveVisibleTotal : filtered.length) !== 1 ? 's' : ''}
          {!isLive && hasActiveFilters && ` (filtered from ${allEntries.length.toLocaleString()} total)`}
        </span>

        {hasActiveFilters && (
          <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
            {dateFrom && (
              <Badge variant="secondary" className="text-[11px] gap-1 h-5 pl-2 pr-1" onClose={() => { setDateFrom(undefined); resetPage(); }}>
                From {format(dateFrom, 'dd MMM yyyy')}
              </Badge>
            )}
            {dateTo && (
              <Badge variant="secondary" className="text-[11px] gap-1 h-5 pl-2 pr-1" onClose={() => { setDateTo(undefined); resetPage(); }}>
                To {format(dateTo, 'dd MMM yyyy')}
              </Badge>
            )}
            {filterUser && (
              <Badge variant="secondary" className="text-[11px] gap-1 h-5 pl-2 pr-1" onClose={() => { setFilterUser(''); resetPage(); }}>
                User: {filterUser}
              </Badge>
            )}
            {filterCategory !== 'all' && (
              <Badge variant="secondary" className="text-[11px] gap-1 h-5 pl-2 pr-1" onClose={() => { setFilterCategory('all'); resetPage(); }}>
                {filterCategory}
              </Badge>
            )}
            {filterAction !== 'all' && (
              <Badge variant="secondary" className="text-[11px] gap-1 h-5 pl-2 pr-1" onClose={() => { setFilterAction('all'); resetPage(); }}>
                {filterAction}
              </Badge>
            )}
            {filterKeyword && (
              <Badge variant="secondary" className="text-[11px] gap-1 h-5 pl-2 pr-1" onClose={() => { setFilterKeyword(''); resetPage(); }}>
                "{filterKeyword}"
              </Badge>
            )}
          </div>
        )}

        <div className="ml-auto flex items-center gap-2 shrink-0">
          {/* Filter button */}
          <Button
            variant={hasActiveFilters ? 'default' : 'outline'}
            size="sm"
            className="relative h-8 w-8 p-0 shrink-0"
            onClick={() => setFilterDrawerOpen(true)}
            aria-label="Open filters"
          >
            <Filter className="h-4 w-4" />
            {hasActiveFilters && (
              <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
                {[dateFrom, dateTo, filterUser || null, filterCategory !== 'all' ? filterCategory : null, filterAction !== 'all' ? filterAction : null, filterKeyword || null].filter(Boolean).length}
              </span>
            )}
          </Button>

          {/* Column config button */}
          <Button
            variant="outline"
            size="sm"
            className="relative h-8 w-8 p-0 shrink-0"
            onClick={() => setColConfigOpen(true)}
            aria-label="Configure columns"
          >
            <SlidersHorizontal className="h-4 w-4" />
            {visibleAuditCols.size < ALL_AUDIT_COLS.length && (
              <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
                {ALL_AUDIT_COLS.length - visibleAuditCols.size}
              </span>
            )}
          </Button>

          {/* Export to Excel */}
          {canExport && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0 shrink-0"
                  onClick={handleExportExcel}
                  aria-label="Export audit log to Excel"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Export to Excel</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          )}
        </div>
      </div>



      {/* ── Column config sheet ─────────────────────────────────────────── */}
      <Sheet open={colConfigOpen} onOpenChange={setColConfigOpen}>
        <SheetContent side="right" className="w-[400px] sm:max-w-[400px] flex flex-col p-0 gap-0">
          <SheetTitle className="sr-only">Configure columns</SheetTitle>
          <SheetDescription className="sr-only">Toggle columns on or off for the audit log table.</SheetDescription>
          <SheetHeader className="px-6 pt-6 pb-5 border-b shrink-0">
            <div className="flex items-center gap-3 pr-8">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                <Settings2 className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold leading-tight">Customise Columns</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">Toggle columns on or off to personalise the audit log table.</p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold tabular-nums shrink-0">{visibleAuditCols.size}</div>
                <span className="text-sm text-muted-foreground">of {ALL_AUDIT_COLS.length} columns showing</span>
              </div>
              <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground" onClick={resetAuditCols}>
                <RotateCcw className="h-3.5 w-3.5" />Reset defaults
              </Button>
            </div>
          </SheetHeader>
          <ScrollArea className="flex-1 min-h-0">
            <ScrollAreaViewport>
              <div className="px-4 py-4 space-y-2">
                {ALL_AUDIT_COLS.map((col) => {
                  const isVisible = visibleAuditCols.has(col.key);
                  return (
                    <div key={col.key} className={['flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors duration-150', isVisible ? 'bg-card border-border hover:border-border/80 hover:bg-accent/30' : 'bg-muted/10 border-border/30'].join(' ')}>
                      <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/30" />
                      <div className="flex-1 min-w-0">
                        <p className={['text-sm font-medium leading-snug', !isVisible ? 'text-muted-foreground' : 'text-foreground'].join(' ')}>{col.label}</p>
                        <p className="text-xs text-muted-foreground/60 mt-0.5 truncate leading-tight">{col.description}</p>
                      </div>
                      <Switch checked={isVisible} onCheckedChange={() => toggleAuditCol(col.key)} aria-label={`${isVisible ? 'Hide' : 'Show'} ${col.label} column`} />
                    </div>
                  );
                })}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
          <SheetFooter className="px-4 py-3 border-t shrink-0">
            <Button className="w-full" onClick={() => setColConfigOpen(false)}>Done</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Filter drawer ─────────────────────────────────────────────────── */}
      <Sheet open={filterDrawerOpen} onOpenChange={setFilterDrawerOpen}>
        <SheetContent side="right" className="w-[380px] sm:max-w-[380px] flex flex-col p-0 gap-0">
          <SheetTitle className="sr-only">Audit log filters</SheetTitle>
          <SheetDescription className="sr-only">Filter audit log by date, user, category, action and keyword.</SheetDescription>
          {/* Header */}
          <SheetHeader className="px-4 pt-4 pb-3 border-b shrink-0 gap-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">Filters</span>
              {hasActiveFilters && (
                <Badge variant="secondary" className="flex items-center gap-1 px-2.5 py-0.5 h-6 text-[11px] font-medium rounded-full bg-primary/10 text-primary border-0 hover:bg-primary/10">
                  <Filter className="h-3 w-3" />
                  {[dateFrom, dateTo, filterUser || null, filterCategory !== 'all' ? filterCategory : null, filterAction !== 'all' ? filterAction : null, filterKeyword || null].filter(Boolean).length}{' '}
                  {[dateFrom, dateTo, filterUser || null, filterCategory !== 'all' ? filterCategory : null, filterAction !== 'all' ? filterAction : null, filterKeyword || null].filter(Boolean).length === 1 ? 'filter' : 'filters'} active
                </Badge>
              )}
            </div>
          </SheetHeader>
          {/* Body */}
          <ScrollArea className="flex-1">
            <ScrollAreaViewport>
              <div className="px-4 py-4 space-y-4">
                {/* Date from */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">From Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="w-full h-9 justify-start text-left font-normal text-[13px]">
                        {dateFrom ? format(dateFrom, 'dd MMM yyyy') : <span className="text-muted-foreground">Pick date…</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={dateFrom} defaultMonth={dateFrom ?? new Date()} onSelect={(d) => { setDateFrom(d); resetPage(); }} captionLayout="dropdown" {...getNavMonths(dateFrom)} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                <Separator />
                {/* Date to */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">To Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="w-full h-9 justify-start text-left font-normal text-[13px]">
                        {dateTo ? format(dateTo, 'dd MMM yyyy') : <span className="text-muted-foreground">Pick date…</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={dateTo} defaultMonth={dateTo ?? dateFrom ?? new Date()} onSelect={(d) => { setDateTo(d); resetPage(); }} captionLayout="dropdown" {...getNavMonths(dateTo ?? dateFrom)} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                <Separator />
                {/* User */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">User</label>
                  <Select value={filterUser || 'all'} onValueChange={(v) => { setFilterUser(v === 'all' ? '' : v); resetPage(); }}>
                    <SelectTrigger className="h-9 w-full text-[13px]">
                      <SelectValue placeholder="All users" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All users</SelectItem>
                      {userOptions.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Separator />
                {/* Category */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Category</label>
                  <Select value={filterCategory} onValueChange={(v) => { setFilterCategory(v); resetPage(); }}>
                    <SelectTrigger className="h-9 w-full text-[13px]">
                      <SelectValue placeholder="All categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All categories</SelectItem>
                      {categoryOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Separator />
                {/* Action */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Action</label>
                  <Select value={filterAction} onValueChange={(v) => { setFilterAction(v); resetPage(); }}>
                    <SelectTrigger className="h-9 w-full text-[13px]">
                      <SelectValue placeholder="All actions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All actions</SelectItem>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="Edit">Edit</SelectItem>
                      <SelectItem value="Delete">Delete</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Separator />
                {/* Keyword */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Keyword / Entity</label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                    <Input
                      value={filterKeyword}
                      onChange={(e) => { setFilterKeyword(e.target.value); resetPage(); }}
                      placeholder="Search entity or changes…"
                      className="h-9 pl-8 text-[13px]"
                    />
                  </div>
                </div>
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
          {/* Footer */}
          <SheetFooter className="px-4 py-3 border-t shrink-0 flex-row items-center justify-between gap-2">
            <Button
              variant="ghost"
              className="h-9 gap-1.5 text-muted-foreground hover:text-foreground px-2"
              onClick={() => { clearFilters(); setFilterDrawerOpen(false); }}
              disabled={!hasActiveFilters}
            >
              <X className="h-3.5 w-3.5" />
              Clear All
            </Button>
            <Button className="h-9 gap-1.5 px-5" onClick={() => setFilterDrawerOpen(false)}>
              <Check className="h-3.5 w-3.5" />
              Apply Filters
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Permission-denied banner ── */}
      {isLive && permissionDenied && (
        <div className="px-6 pt-5 shrink-0">
          <div
            className="flex items-start gap-3 rounded-[var(--radius-lg)] bg-destructive/10 px-4 py-3"
            style={{ border: '0.5px solid var(--color-border-tertiary, var(--border))' }}
            role="alert"
          >
            <ShieldAlert className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[13px] font-medium text-foreground">
                You do not have permission to view the audit log
              </p>
              <p className="text-[12px] text-muted-foreground mt-0.5">
                Contact your administrator if you believe you should have access to this report.
              </p>
            </div>
          </div>
        </div>
      )}

      {isLive && liveLoading && <PageLoadingOverlay message="Loading audit log..." />}

      {/* ── Table ── */}
      <div ref={contentRef} className="flex-1 min-h-0 overflow-auto px-6 py-5">
        <div
          className={RAW_TABLE_CONTAINER_CLASS}
        >
          <div className={RAW_TABLE_SCROLL_CLASS}>
            <table className={RAW_TABLE_CLASS}>
              <thead>
                <tr className={TABLE_HEADER_ROW_CLASS}>
                  {/* Expand toggle — narrow, no label */}
                  <th className="w-8 px-2 py-2.5" />
                  <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left whitespace-nowrap cursor-pointer select-none" onClick={() => handleSort('timestamp')}>
                    <span className="inline-flex items-center">Date / Time <SortIcon field="timestamp" /></span>
                  </th>
                  {visibleAuditCols.has('user') && (
                    <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left whitespace-nowrap cursor-pointer select-none" onClick={() => handleSort('user')}>
                      <span className="inline-flex items-center">User <SortIcon field="user" /></span>
                    </th>
                  )}
                  {visibleAuditCols.has('category') && (
                    <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left whitespace-nowrap cursor-pointer select-none" onClick={() => handleSort('category')}>
                      <span className="inline-flex items-center">Category <SortIcon field="category" /></span>
                    </th>
                  )}
                  {visibleAuditCols.has('action') && (
                    <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left whitespace-nowrap cursor-pointer select-none" onClick={() => handleSort('action')}>
                      <span className="inline-flex items-center">Action <SortIcon field="action" /></span>
                    </th>
                  )}
                  {visibleAuditCols.has('entity') && (
                    <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left whitespace-nowrap cursor-pointer select-none" onClick={() => handleSort('entity')}>
                      <span className="inline-flex items-center">Entity <SortIcon field="entity" /></span>
                    </th>
                  )}
                  {visibleAuditCols.has('before') && (
                    <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left whitespace-nowrap" style={{ minWidth: 180 }}>Before</th>
                  )}
                  {visibleAuditCols.has('after') && (
                    <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left whitespace-nowrap" style={{ minWidth: 180 }}>After</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {paginatedEntries.length === 0 && (
                  <tr>
                    <td
                      colSpan={2 + visibleAuditCols.size}
                      className="text-center py-16 text-[13px] text-muted-foreground"
                    >
                      {hasActiveFilters
                        ? 'No audit log records match the current filters.'
                        : 'No audit log records found.'}
                    </td>
                  </tr>
                )}

                {paginatedEntries.map((entry, rowIdx) => {
                  const changeCount = entry.changes.length;
                  const isExpanded = expandedIds.has(entry.id);
                  const isMulti = changeCount > 1;
                  const isSingle = changeCount === 1;
                  const firstChange = isSingle ? entry.changes[0] : null;
                  const rowBg = alternatingRowBackground(rowIdx);

                  return (
                    <React.Fragment key={entry.id}>
                      {/* ── Primary (summary) row ── */}
                      <tr
                        style={{ height: 48, background: rowBg }}
                        className="transition-colors"
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = TABLE_HOVER_BG; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = rowBg; }}
                      >
                        {/* Expand chevron */}
                        <td className="w-8 px-2 py-2">
                          {isMulti ? (
                            <button
                              onClick={() => toggleExpand(entry.id)}
                              aria-label={isExpanded ? 'Collapse changes' : 'Expand changes'}
                              className="flex items-center justify-center h-5 w-5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                            >
                              {isExpanded
                                ? <ChevronDown className="h-3.5 w-3.5" />
                                : <ChevronRightExpand className="h-3.5 w-3.5" />
                              }
                            </button>
                          ) : null}
                        </td>

                        {/* Date / Time */}
                        <td className="px-3 py-2 whitespace-nowrap text-[13px] tabular-nums text-muted-foreground">
                          {formatDateTime(entry.timestamp)}
                        </td>

                        {/* User */}
                        {visibleAuditCols.has('user') && (
                          <td className="px-3 py-2 text-[13px] font-medium text-foreground whitespace-nowrap">
                            {entry.user}
                          </td>
                        )}

                        {/* Category */}
                        {visibleAuditCols.has('category') && (
                          <td className="px-3 py-2">
                            <CategoryPill label={entry.category} />
                          </td>
                        )}

                        {/* Action */}
                        {visibleAuditCols.has('action') && (
                          <td className="px-3 py-2">
                            <ActionBadge action={entry.action} />
                          </td>
                        )}

                        {/* Entity */}
                        {visibleAuditCols.has('entity') && (
                          <td className="px-3 py-2 text-[13px] font-medium text-foreground max-w-[200px] truncate" title={entry.entity}>
                            {entry.entity}
                          </td>
                        )}

                        {/* Before */}
                        {visibleAuditCols.has('before') && (
                          <td className="px-3 py-2 max-w-[220px]">
                            {isSingle && firstChange ? (
                              <BeforeCell action={entry.action} value={firstChange.before} />
                            ) : isMulti ? (
                              <span className="text-[12px] text-muted-foreground italic">{fieldSummary(entry)}</span>
                            ) : (
                              <span className="text-[13px] text-muted-foreground/40 italic">—</span>
                            )}
                          </td>
                        )}

                        {/* After */}
                        {visibleAuditCols.has('after') && (
                          <td className="px-3 py-2 max-w-[220px]">
                            {isSingle && firstChange ? (
                              <AfterCell action={entry.action} value={firstChange.after} />
                            ) : isMulti ? (
                              isExpanded ? (
                                <span className="text-[12px] text-muted-foreground">{entry.changes.length} fields changed</span>
                              ) : null
                            ) : entry.action === 'Delete' ? (
                              <Badge variant="destructive" accent="destructive" className="text-[11px] font-medium">Deleted</Badge>
                            ) : (
                              <span className="text-[13px] text-muted-foreground/40 italic">—</span>
                            )}
                          </td>
                        )}
                      </tr>

                      {/* ── Expanded field-level detail rows ── */}
                      {isMulti && isExpanded && entry.changes.map((ch, idx) => (
                        <tr
                          key={`${entry.id}-exp-${idx}`}
                          style={{ background: 'var(--color-background-secondary, var(--muted))' }}
                        >
                          <td className="w-8 px-2 py-2" />
                          <td className="px-3 py-2" />
                          {visibleAuditCols.has('user') && <td className="px-3 py-2" />}
                          {visibleAuditCols.has('category') && <td className="px-3 py-2" />}
                          {visibleAuditCols.has('action') && <td className="px-3 py-2" />}
                          {visibleAuditCols.has('entity') && (
                            <td className="px-3 py-2 pl-6">
                              <span className="text-[12px] font-medium text-muted-foreground">{ch.field}</span>
                            </td>
                          )}
                          {visibleAuditCols.has('before') && (
                            <td className="px-3 py-2 max-w-[220px]">
                              <BeforeCell action={entry.action} value={ch.before} />
                            </td>
                          )}
                          {visibleAuditCols.has('after') && (
                            <td className="px-3 py-2 max-w-[220px]">
                              <AfterCell action={entry.action} value={ch.after} />
                            </td>
                          )}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Pagination ── */}
        <div className="pt-4">
          <TablePagination
            page={page}
            pageSize={pageSize}
            totalItems={totalRecords}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            itemLabel="records"
            alwaysShow
          />
        </div>
      </div>
    </div>
  );
}
